import classNames from 'clsx';
import {
  type FocusEvent,
  forwardRef,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PropsWithChildren,
  useRef,
  useState,
  type WheelEvent,
} from 'react';

import { chartEventsWrapper } from './InteractionsOverlay.sty.js';
import type {
  InteractionMode,
  Interactions,
  InteractionsOverlayProps,
  OverlappingInteractionsProps,
} from './types.js';
import { MIDDLE_BUTTON_ID } from '../../constants.js';
import { getActiveMode } from './utils/get-active-mode.js';
import { getExploreInteractions } from './utils/get-explore-interations.js';
import { getGestures } from './utils/get-gestures.js';
import { getInterfaceInteractions } from './utils/get-interface-interactions.js';
import { getPanInteractions } from './utils/get-pan-interactions.js';
import { getZoomInteractions } from './utils/get-zoom-interactions.js';
import { handleKeyDown } from './utils/handle-keydown.js';
import { isOwnEvent } from './utils/is-own-event.js';
import { useMergeRefs } from '../../../../core/hooks/useMergeRefs.js';
import { usePointerEvents } from '../../hooks/usePointerEvents.js';
import { usePreventScrollOnWheel } from '../../hooks/usePreventScrollOnWheel.js';
import type { RelativeAbsolutePosition } from '../../types/pointer-events.js';

const isDragDisabled = (
  effectiveMode: InteractionMode,
  panDisabled: boolean | undefined,
  zoomDisabled: boolean | undefined,
  isSelectDisabled: boolean | undefined,
): boolean | undefined => {
  if (effectiveMode === 'pan') {
    return panDisabled;
  }
  if (effectiveMode === 'zoom') {
    return zoomDisabled;
  }
  return isSelectDisabled;
};

/**
 * @internal
 * @param props -
 */
export const InteractionsOverlay = forwardRef<
  HTMLDivElement,
  PropsWithChildren<InteractionsOverlayProps>
>((props, forwardedRef) => {
  const {
    style,
    className,
    mode,
    pinned,
    selectionArea,
    width,
    height,
    onModeChange,
    onFocus,
    onHover,
    onLeave,
    onPanStart,
    onPanChange,
    onPanEnd,
    onTogglePin,
    onReset,
    onSelectStart,
    onSelectChange,
    onSelectEnd,
    onZoomStart,
    onZoomChange,
    onZoomEnd,
    isZoomDisabled,
    isPanDisabled,
    isSelectDisabled,
    isZoomXDisabled,
    children,
    ...restProps
  } = props;

  const zoomDisabled = selectionArea ? true : props.isZoomDisabled;
  const panDisabled = selectionArea ? true : props.isPanDisabled;

  //#region Internal state
  /**
   * Keeps track of full pan delta interaction in pixels
   */
  const panDeltaRef = useRef<number>(undefined);
  /**
   * Keeps track of mouse position
   */
  const lastMousePositionRef = useRef<RelativeAbsolutePosition>(undefined);

  const isPointerDownRef = useRef(false);

  const eventCancelled = useRef(false);

  const handlerRef = useRef<null | (() => void)>(null);

  //#endregion

  const [cursorMode, setCursorMode] = useState<InteractionMode | 'zoomOut'>(
    mode,
  );

  const htmlRef = useRef<HTMLDivElement>(null);

  usePreventScrollOnWheel(htmlRef);

  const rootRef = useMergeRefs([htmlRef, forwardedRef]);

  const overlappingInteractionsProps: OverlappingInteractionsProps = {
    selectionArea,
    eventCancelled,
    onSelectEnd,
    lastMousePositionRef,
    pinned,
    onHover,
    onTogglePin,
    setCursorMode,
    height,
    onReset,
    width,
    onZoomChange,
    onPanChange,
    panDeltaRef,
    onPanStart,
    onPanEnd,
  };

  //#region Native HTML events
  const handleMouseLeave = () => {
    onLeave?.({ triggeredBy: 'pointer' });
    isPointerDownRef.current = false;
    lastMousePositionRef.current = undefined;
  };

  const handleFocus = (event?: FocusEvent) => {
    if (handlerRef.current) {
      handlerRef.current();
      handlerRef.current = null;
    }
    onFocus?.(event);
  };

  const handleBlur = (event: FocusEvent) => {
    const nextFocusElement = event.relatedTarget;

    if (!event.target?.contains(nextFocusElement)) {
      onLeave?.({ triggeredBy: 'keyboard' });
    }
  };

  const handleWheel = (event: WheelEvent) => {
    interactions[getActiveMode(mode, event, zoomDisabled)].onWheel(event);
  };

  const handleKeyboard = (event: KeyboardEvent) =>
    handleKeyDown({
      event,
      lastMousePositionRef,
      panDeltaRef,
      selectionArea,
      eventCancelled,
      onSelectEnd,
      onModeChange,
      interaction:
        interactions[getActiveMode(mode, event, zoomDisabled, isPanDisabled)],
    });
  //#endregion

  //#region Interactions strategy
  const interactions: Interactions = {
    explore: getExploreInteractions({
      ...overlappingInteractionsProps,
      onModeChange,
      onSelectChange,
      onSelectStart,
      isZoomXDisabled,
    }),
    zoom: getZoomInteractions({
      ...overlappingInteractionsProps,
      cursorMode,
      onSelectChange,
      onSelectStart,
    }),
    pan: getPanInteractions({
      ...overlappingInteractionsProps,
      onModeChange,
      cursorMode,
    }),
  };
  //#endregion

  const {
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
  } = usePointerEvents({
    ...getInterfaceInteractions(
      interactions,
      mode,
      zoomDisabled,
      isZoomXDisabled,
      panDisabled,
    ),
    ...getGestures({
      lastMousePositionRef,
      panDeltaRef,
      onZoomStart,
      onZoomChange,
      onZoomEnd,
      onPanStart,
      onPanChange,
      onPanEnd,
    }),
    eventCancelled,
  });

  const handleDocumentMouseUp = (e: MouseEvent) => {
    const { clientX, clientY } = e;
    if (htmlRef?.current) {
      handleMouseUp({
        ...(e as unknown as ReactMouseEvent<HTMLDivElement>),
        currentTarget: htmlRef.current,
        clientX,
        clientY,
      });

      isPointerDownRef.current = false;
      lastMousePositionRef.current = undefined;
    }
  };

  const handleDocumentMouseMove = (e: MouseEvent) => {
    const { clientX, clientY } = e;
    if (htmlRef?.current) {
      handleMouseMove({
        ...(e as unknown as ReactMouseEvent<HTMLDivElement>),
        currentTarget: htmlRef.current,
        clientX: clientX,
        clientY,
      });
    }
  };

  const registerDocumentListeners = () => {
    if (!handlerRef.current) {
      document.addEventListener('mouseup', handleDocumentMouseUp);
      document.addEventListener('pointermove', handleDocumentMouseMove);
      handlerRef.current = () => {
        document.removeEventListener('mouseup', handleDocumentMouseUp);
        document.removeEventListener('pointermove', handleDocumentMouseMove);
      };
    }
  };

  return (
    <div
      ref={rootRef}
      tabIndex={0}
      className={classNames(
        chartEventsWrapper({ mode: cursorMode }),
        className,
      )}
      style={{
        ...style,
        cursor: cursorMode === 'explore' ? style?.cursor : undefined,
      }}
      onMouseDown={(event) => {
        if (isOwnEvent(event)) {
          const isMiddleButton = event.buttons === MIDDLE_BUTTON_ID;

          if (!isMiddleButton) {
            const effectiveMode = getActiveMode(
              mode,
              event,
              zoomDisabled,
              panDisabled,
            );

            if (
              isDragDisabled(
                effectiveMode,
                panDisabled,
                zoomDisabled,
                isSelectDisabled,
              )
            ) {
              return;
            }
          }

          isPointerDownRef.current = true;
          handleMouseDown(event);
        }
      }}
      onMouseMove={(event) => {
        if (isOwnEvent(event)) {
          handleMouseMove(event);
        }
      }}
      onMouseUp={(event) => {
        if (isOwnEvent(event)) {
          handleMouseUp(event);
          isPointerDownRef.current = false;
        }
      }}
      onMouseLeave={(e) => {
        if (
          isPointerDownRef.current &&
          lastMousePositionRef.current &&
          htmlRef.current
        ) {
          registerDocumentListeners();
        } else {
          handleMouseLeave();
        }
      }}
      onTouchStart={(event) => {
        if (isOwnEvent(event)) {
          const effectiveMode = getActiveMode(
            mode,
            event,
            zoomDisabled,
            panDisabled,
          );

          if (
            !isDragDisabled(
              effectiveMode,
              panDisabled,
              zoomDisabled,
              isSelectDisabled,
            )
          ) {
            handleTouchStart(event);
          }
        }
      }}
      onTouchMove={(event) => {
        if (isOwnEvent(event)) {
          handleTouchMove(event);
        }
      }}
      onTouchEnd={(event) => {
        if (isOwnEvent(event)) {
          handleTouchEnd(event);
        }
      }}
      onMouseEnter={() => handleFocus()}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onKeyDown={handleKeyboard}
      onWheel={handleWheel}
      {...restProps}
    >
      {children}
    </div>
  );
});

(InteractionsOverlay as typeof InteractionsOverlay & { displayName: string })[
  'displayName'
] = 'InteractionsOverlay';
